import { createTheme } from '@mui/material/styles';
import { primitive, getSemanticTokens, getComponentTokens } from './tokens';

const { typography: typographyPrimitives, motion, radius } = primitive;

export function createAppTheme(mode = 'light', branding = {}) {
  const { primaryColor, accentColor, fontFamily } = branding || {};
  const semantic = getSemanticTokens(mode, primaryColor, accentColor);
  const components = getComponentTokens(semantic);

  const theme = createTheme({
    palette: {
      mode,
      primary: {
        main: semantic.action.primary,
        contrastText: semantic.action.onPrimary,
      },
      secondary: {
        main: primitive.color.warm[500],
        contrastText: '#FFFFFF',
      },
      success: { main: semantic.status.positive },
      warning: { main: semantic.status.warning },
      error: { main: semantic.status.danger },
      info: { main: semantic.status.info },
      background: {
        default: semantic.surface.canvas,
        paper: semantic.surface.base,
      },
      text: {
        primary: semantic.content.primary,
        secondary: semantic.content.secondary,
        disabled: semantic.content.muted,
      },
      divider: semantic.border.subtle,
    },
    typography: {
      fontFamily: typographyPrimitives.fontFamilyUI,
      h1: { fontFamily: typographyPrimitives.fontFamilyDisplay, ...typographyPrimitives.scale.displayXl },
      h2: { fontFamily: typographyPrimitives.fontFamilyDisplay, ...typographyPrimitives.scale.displayMd },
      h3: { fontFamily: typographyPrimitives.fontFamilyDisplay, ...typographyPrimitives.scale.headingXl },
      h4: { fontFamily: typographyPrimitives.fontFamilyDisplay, ...typographyPrimitives.scale.headingXl },
      h5: { fontFamily: typographyPrimitives.fontFamilyDisplay, ...typographyPrimitives.scale.headingMd },
      h6: { fontFamily: typographyPrimitives.fontFamilyDisplay, ...typographyPrimitives.scale.headingMd },
      body1: typographyPrimitives.scale.bodyLg,
      body2: typographyPrimitives.scale.bodyMd,
      button: { ...typographyPrimitives.scale.label, textTransform: 'none' },
      caption: typographyPrimitives.scale.micro,
    },
    shape: {
      borderRadius: radius.control,
    },
    transitions: {
      duration: {
        shortest: motion.duration.instant,
        shorter: motion.duration.fast,
        short: motion.duration.fast,
        standard: motion.duration.standard,
        complex: motion.duration.emphasis,
      },
      easing: {
        easeInOut: motion.easing.standard,
        easeOut: motion.easing.emphasis,
        easeIn: motion.easing.exit,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: radius.control,
            padding: '10px 22px',
            boxShadow: 'none',
            fontWeight: 650,
          },
          contained: {
            boxShadow: 'none',
            '&:hover': { boxShadow: semantic.shadow.soft, backgroundColor: semantic.action.primaryHover },
          },
          containedPrimary: {
            backgroundColor: semantic.action.primary,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: radius.card,
            boxShadow: semantic.shadow.soft,
            border: `1px solid ${semantic.border.subtle}`,
            backgroundColor: semantic.surface.base,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: radius.card,
            backgroundImage: 'none',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: radius.pill,
            fontWeight: 600,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: radius.control,
            backgroundColor: semantic.surface.base,
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: radius.pill,
            backgroundColor: semantic.progress.track,
          },
          bar: {
            borderRadius: radius.pill,
            backgroundColor: semantic.progress.fill,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: `1px solid ${semantic.border.subtle}`,
            backgroundColor: components.navigation.sidebarBackground,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': { backgroundColor: semantic.surface.brandTint },
          },
        },
      },
    },
  });

  theme.tokens = { semantic, components: getComponentTokens(semantic), primitive };

  if (fontFamily) {
    theme.typography.fontFamily = fontFamily;
    theme.typography.h1.fontFamily = fontFamily;
    theme.typography.h2.fontFamily = fontFamily;
    theme.typography.h3.fontFamily = fontFamily;
    theme.typography.h4.fontFamily = fontFamily;
    theme.typography.h5.fontFamily = fontFamily;
    theme.typography.h6.fontFamily = fontFamily;
  }

  return theme;
}

export const lightTheme = createAppTheme('light');
export const darkTheme = createAppTheme('dark');
